import { logEvent } from "./utils.js";

const LOGGING_CONFIG = {
    REPORT_URL_INDICES: {
        FORMAT: 1,
        ID: 2,
        TYPE: 3,
    },
    DEFAULT_VALUES: {
        REPORT_TYPE: "parent",
        REPORT_ID: "",
        REPORT_FORMAT: null,
    },
    PARAMETERS: {
        ECP_SUFFIX: "-ecp",
        VISION_SIMULATOR: "click-vision-simulator",
        TOOLTIP: "click-info",
        ACCORDION: "expand-section",
        EYE_TOGGLE: "click-eye",
    },
    SUBSECTIONS: {
        MEYE_GAUGE: "meye-gauge",
        MEYE_GUIDE: "meye-guide",
        MEYE_SCORE: "meye-score",
    },
};

const REPORT_FORMAT_MAPPINGS = {
    "qr-meye": LOGGING_CONFIG.SUBSECTIONS.MEYE_GUIDE,
    "qr-score": LOGGING_CONFIG.SUBSECTIONS.MEYE_SCORE,
};

const MYOPIA_COMPARE_SECTION = "myopia-compare";
const VISION_SIMULATOR_SECTION = "vision-simulator";
const MYOPIA_MANAGEMENT_HELP_SECTION = "myopia-management-help";
const AXIAL_LENGTH_ANALYSIS_SECTION = "axial-length-analysis";
const TREATMENTS_AVAILABLE_SECTION = "treatments-available";
const WHAT_ELSE_SECTION = "what-else-can-we-do";
const MYOPIA_CHANGED_SECTION = "myopia-changed";
const MYOPIA_EYES_GROWN = "eyes-grown";
const IMPORTANT_TO_REMEMBER_SECTION = "important-to-remember";
const TREATMENT_SUMMARY = "treatment-summary";
const SCORE_CARD = "score-card";
const TREATMENT_OPTIMIZATION = "treatment-optimization";

const EVENT_TYPE_CONTENT = "content";

const ELEMENT_ID_TO_SECTION_MAPPING = [
    {
        patterns: [MYOPIA_COMPARE_SECTION, "compare-md"],
        sectionID: MYOPIA_COMPARE_SECTION,
        subsectionID: null,
        parseEye: true,
    },
    {
        patterns: ["vision-simulator-button", "eye-simulator-tab"],
        sectionID: VISION_SIMULATOR_SECTION,
        subsectionID: null,
        parseEye: false,
    },
    {
        patterns: ["vision-simulator-now"],
        sectionID: VISION_SIMULATOR_SECTION,
        subsectionID: "sees-now",
        parseEye: true,
    },
    {
        patterns: ["vision-simulator-adult"],
        sectionID: VISION_SIMULATOR_SECTION,
        subsectionID: "sees-as-adult",
        parseEye: true,
    },
    {
        patterns: [MYOPIA_MANAGEMENT_HELP_SECTION, "myopia-help-tab"],
        sectionID: MYOPIA_MANAGEMENT_HELP_SECTION,
        subsectionID: null,
        parseEye: false,
    },
    {
        patterns: [
            AXIAL_LENGTH_ANALYSIS_SECTION,
            "tooltip-axial-length",
            "axial-length-eyes",
        ],
        sectionID: AXIAL_LENGTH_ANALYSIS_SECTION,
        subsectionID: null,
        parseEye: true,
    },
    {
        patterns: ["axial-length-treatment-targets"],
        sectionID: AXIAL_LENGTH_ANALYSIS_SECTION,
        subsectionID: "treatment-targets",
        parseEye: true,
    },
    {
        patterns: [TREATMENTS_AVAILABLE_SECTION],
        sectionID: TREATMENTS_AVAILABLE_SECTION,
        subsectionID: null,
        parseEye: false,
    },
    {
        patterns: ["available-treatments-spectacles"],
        sectionID: TREATMENTS_AVAILABLE_SECTION,
        subsectionID: "spectacles",
        parseEye: false,
    },
    {
        patterns: ["available-treatments-contact-lenses"],
        sectionID: TREATMENTS_AVAILABLE_SECTION,
        subsectionID: "contact-lenses",
        parseEye: false,
    },
    {
        patterns: ["available-treatments-overnight-contact-lenses"],
        sectionID: TREATMENTS_AVAILABLE_SECTION,
        subsectionID: "overnight-contact-lenses",
        parseEye: false,
    },
    {
        patterns: ["available-treatments-controlled-release-lenses"],
        sectionID: TREATMENTS_AVAILABLE_SECTION,
        subsectionID: "controlled-release-contact-lenses",
        parseEye: false,
    },
    {
        patterns: ["available-treatments-eye-drops"],
        sectionID: TREATMENTS_AVAILABLE_SECTION,
        subsectionID: "eye-drops",
        parseEye: false,
    },
    {
        patterns: [WHAT_ELSE_SECTION],
        sectionID: WHAT_ELSE_SECTION,
        subsectionID: null,
        parseEye: false,
    },
    {
        patterns: ["what-else-outdoor-time"],
        sectionID: WHAT_ELSE_SECTION,
        subsectionID: "outdoor-time",
        parseEye: false,
    },
    {
        patterns: ["what-else-near-work"],
        sectionID: WHAT_ELSE_SECTION,
        subsectionID: "near-work",
        parseEye: false,
    },
    {
        patterns: ["what-else-screen-time"],
        sectionID: WHAT_ELSE_SECTION,
        subsectionID: "screen-time",
        parseEye: false,
    },
    {
        patterns: ["what-else-eye-checks"],
        sectionID: WHAT_ELSE_SECTION,
        subsectionID: "eye-checks",
        parseEye: false,
    },
    {
        patterns: [MYOPIA_CHANGED_SECTION],
        sectionID: MYOPIA_CHANGED_SECTION,
        subsectionID: null,
        parseEye: true,
    },
    {
        patterns: ["overall-progression-trigger"],
        sectionID: MYOPIA_CHANGED_SECTION,
        subsectionID: "overall-progression",
        parseEye: true,
    },
    {
        patterns: ["treatment-benefit-trigger"],
        sectionID: MYOPIA_CHANGED_SECTION,
        subsectionID: "treatment-benefit",
        parseEye: true,
    },
    {
        patterns: ["treatment-stability-trigger"],
        sectionID: MYOPIA_CHANGED_SECTION,
        subsectionID: "treatment-stability",
        parseEye: true,
    },
    {
        patterns: [MYOPIA_EYES_GROWN],
        sectionID: MYOPIA_EYES_GROWN,
        subsectionID: null,
        parseEye: true,
    },
    {
        patterns: ["first-chart-myopia-graph"],
        sectionID: MYOPIA_CHANGED_SECTION,
        subsectionID: "first-chart",
        parseEye: true,
    },
    {
        patterns: ["overall-efficacy-trigger"],
        sectionID: MYOPIA_CHANGED_SECTION,
        subsectionID: "overall-efficacy",
        parseEye: true,
    },
    {
        patterns: ["centile-chart-myopia-graph"],
        sectionID: MYOPIA_CHANGED_SECTION,
        subsectionID: "centile-chart",
        parseEye: true,
    },
    {
        patterns: ["first-chart-axial-length-graph"],
        sectionID: MYOPIA_EYES_GROWN,
        subsectionID: "first-chart",
        parseEye: true,
    },
    {
        patterns: ["centile-chart-axial-length-graph"],
        sectionID: MYOPIA_EYES_GROWN,
        subsectionID: "centile-chart",
        parseEye: true,
    },
    {
        patterns: ["overall-growth-progression-trigger"],
        sectionID: MYOPIA_EYES_GROWN,
        subsectionID: "overall-growth-progression",
        parseEye: true,
    },
    {
        patterns: ["treatment-growth-benefit-trigger"],
        sectionID: MYOPIA_EYES_GROWN,
        subsectionID: "treatment-growth-progression",
        parseEye: true,
    },
    {
        patterns: ["treatment-growth-stability-trigger"],
        sectionID: MYOPIA_EYES_GROWN,
        subsectionID: "treatment-stability",
        parseEye: true,
    },
    {
        patterns: ["treatment-summary-accordion"],
        sectionID: TREATMENT_SUMMARY,
        subsectionID: null,
        parseEye: false,
    },
    {
        patterns: [SCORE_CARD],
        sectionID: SCORE_CARD,
        subsectionID: null,
        parseEye: true,
    },
    {
        patterns: ["tooltip-score-treatment-benefit"],
        sectionID: SCORE_CARD,
        subsectionID: "short-followup",
        parseEye: true,
    },
    {
        patterns: [IMPORTANT_TO_REMEMBER_SECTION],
        sectionID: IMPORTANT_TO_REMEMBER_SECTION,
        subsectionID: null,
        parseEye: false,
    },
    {
        patterns: ["collapse-treatment-info-"],
        sectionID: IMPORTANT_TO_REMEMBER_SECTION,
        subsectionSource: "content-info",
        parseEye: false,
    },
    {
        patterns: ["treatment-optimization-button"],
        sectionID: TREATMENT_OPTIMIZATION,
        subsectionID: null,
        parseEye: false,
    },
    {
        patterns: ["collapse-treatment-trigger"],
        sectionID: TREATMENT_OPTIMIZATION,
        subsectionID: "use-treatment-correctly",
        parseEye: false,
    },
    {
        patterns: ["collapse-screentime-trigger"],
        sectionID: TREATMENT_OPTIMIZATION,
        subsectionID: "screentime-and-reading",
        parseEye: false,
    },
    {
        patterns: ["collapse-outdoors-trigger"],
        sectionID: TREATMENT_OPTIMIZATION,
        subsectionID: "time-outdoors",
        parseEye: false,
    },
];

const SECTION_PRODUCTS = "products-button";
const SECTION_MENU = "products-menu";
const NAVIGATION_ID_TO_SECTION_MAPPING = [
    { patterns: ["display-tac"], sectionID: "display-tac", subsectionID: "" },
    {
        patterns: ["display-privacy"],
        sectionID: "display-privacy",
        subsectionID: "",
    },
    {
        patterns: ["ocumetra-home-button"],
        sectionID: "home-button",
        subsectionID: "",
    },
    {
        patterns: [
            "navbar-products-desktop-button",
            "navbar-products-mobile-button",
        ],
        sectionID: SECTION_MENU,
        subsectionID: "",
    },
    {
        patterns: ["navbar-products-meye-gauge"],
        sectionID: SECTION_MENU,
        subsectionID: LOGGING_CONFIG.SUBSECTIONS.MEYE_GAUGE,
    },
    {
        patterns: ["navbar-products-meye-guide"],
        sectionID: SECTION_MENU,
        subsectionID: LOGGING_CONFIG.SUBSECTIONS.MEYE_GUIDE,
    },
    {
        patterns: ["navbar-products-meye-score"],
        sectionID: SECTION_MENU,
        subsectionID: LOGGING_CONFIG.SUBSECTIONS.MEYE_SCORE,
    },
    {
        patterns: ["navbar-resources-button"],
        sectionID: "menu-button",
        subsectionID: "resources",
    },
    {
        patterns: ["navbar-reports-button"],
        sectionID: "menu-button",
        subsectionID: "reports",
    },
    {
        patterns: ["navbar-language-button"],
        sectionID: "language",
        subsectionID: "",
    },
    {
        patterns: ["navbar-language-link"],
        sectionID: "language",
        subsectionSource: "data-lang-code",
    },
    {
        patterns: ["launch-gauge-button"],
        sectionID: SECTION_PRODUCTS,
        subsectionID: LOGGING_CONFIG.SUBSECTIONS.MEYE_GAUGE,
    },
    {
        patterns: ["launch-guide-button"],
        sectionID: SECTION_PRODUCTS,
        subsectionID: LOGGING_CONFIG.SUBSECTIONS.MEYE_GUIDE,
    },
    {
        patterns: ["launch-score-button"],
        sectionID: SECTION_PRODUCTS,
        subsectionID: LOGGING_CONFIG.SUBSECTIONS.MEYE_SCORE,
    },
    {
        patterns: ["upgrade-guide-button"],
        sectionID: SECTION_PRODUCTS,
        subsectionID: "upgrade-guide",
    },
    {
        patterns: ["upgrade-score-button"],
        sectionID: SECTION_PRODUCTS,
        subsectionID: "upgrade-score",
    },
    {
        patterns: ["products-button-resources"],
        sectionID: SECTION_PRODUCTS,
        subsectionID: "resources",
    },
    {
        patterns: ["navbar-account-button"],
        sectionID: "account",
        subsectionID: "",
    },
    {
        patterns: ["navbar-account-manage"],
        sectionID: "account",
        subsectionID: "manage",
    },
    {
        patterns: ["navbar-account-license"],
        sectionID: "account",
        subsectionID: "license",
    },
    {
        patterns: ["navbar-account-logout-button"],
        sectionID: "account",
        subsectionID: "sign-out",
    },
    {
        patterns: ["theme-mode-switch-trigger"],
        sectionID: "mode-switch",
        subsectionID: "",
    },
    // mappings used in report buttons
    {
        patterns: ["science-behind-link"],
        sectionID: "science-behind",
        subsectionID: null,
    },
    {
        patterns: ["new-patient-button"],
        sectionID: "new-patient",
        subsectionID: null,
    },
    {
        patterns: ["consent-form-button"],
        sectionID: "consent-form",
        subsectionID: null,
    },
    {
        patterns: ["add-score-visit", "guide-to-score-link"],
        sectionID: "add-visit",
        subsectionID: null,
    },
];

export function initReportLogging() {
    const loggableButtons = document.querySelectorAll(
        "[class*='report-loggable-button']"
    );
    loggableButtons.forEach((link) => {
        link.addEventListener("click", clickLoggableReportButton);
    });

    const accordionButtons = document.querySelectorAll(
        "[class*='meye-accordion']"
    );
    accordionButtons.forEach((accordionButton) => {
        accordionButton.addEventListener("click", clickMeyeAccordion);
    });

    const tooltipTriggers = document.querySelectorAll(
        "[class*='tooltip-trigger']"
    );
    tooltipTriggers.forEach((tooltipTrigger) => {
        tooltipTrigger.addEventListener("mouseenter", hoverMeyeTooltip);
        tooltipTrigger.addEventListener("click", hoverMeyeTooltip);
    });

    const eyeToggles = document.querySelectorAll("[class*='eye-toggle-radio']");
    eyeToggles.forEach((eyeToggle) => {
        eyeToggle.addEventListener("click", clickEyeToggle);
    });

    const visionSimulators = document.querySelectorAll(
        "[class*='vision-sim-container']"
    );
    visionSimulators.forEach((visionSimulator) => {
        visionSimulator.addEventListener("click", clickVisionSimulator);
    });
}

export function initContentLogging() {
    const contentCtas = document.querySelectorAll("[class^='content-cta']");
    contentCtas.forEach((contentCta) => {
        contentCta.addEventListener("click", clickContentCta);
    });

    const contentQuestions = document.querySelectorAll(
        "[class^='content-question-accordion']"
    );
    contentQuestions.forEach((contentQuestion) => {
        contentQuestion.addEventListener("click", clickContentQuestion);
    });

    const popupButtons = document.querySelectorAll(
        "[class*='loggable-navigation-trigger']"
    );
    popupButtons.forEach((popup) => {
        popup.addEventListener("click", logNavigationTrigger);
    });
}

function logNavigationTrigger(event) {
    const element = event.currentTarget;
    const response = navigationSectionFromElement(element);
    if (response !== null) {
        if (response.section_id === "mode-switch") {
            const modeInfoElement = document.getElementById(
                "theme-mode-switch-value"
            );
            const mode = modeInfoElement.getAttribute("value");
            logEvent("navigation", response.section_id, mode, "");
            return;
        }
        logEvent("navigation", response.section_id, response.subsection_id, "");
    }
}

function getReportDetailsFromURL() {
    const currentUrl = new URL(window.location.href);
    const pathSegments = currentUrl.pathname.split("/");

    const reportFormatPath =
        pathSegments[LOGGING_CONFIG.REPORT_URL_INDICES.FORMAT];
    const reportFormat =
        REPORT_FORMAT_MAPPINGS[reportFormatPath] ||
        LOGGING_CONFIG.DEFAULT_VALUES.REPORT_FORMAT;

    const reportID =
        pathSegments.length > LOGGING_CONFIG.REPORT_URL_INDICES.ID
            ? pathSegments[LOGGING_CONFIG.REPORT_URL_INDICES.ID]
            : LOGGING_CONFIG.DEFAULT_VALUES.REPORT_ID;

    const reportType =
        pathSegments.length > LOGGING_CONFIG.REPORT_URL_INDICES.TYPE
            ? pathSegments[LOGGING_CONFIG.REPORT_URL_INDICES.TYPE]
            : LOGGING_CONFIG.DEFAULT_VALUES.REPORT_TYPE;

    return [reportFormat, reportID, reportType];
}

// THis is different to the loggable navigation trigger as this also needs to extract
// the report ID and type.
function clickLoggableReportButton(event) {
    const [reportFormat, reportID, reportType] = getReportDetailsFromURL();
    if (reportFormat !== null) {
        const sectionInfo = navigationSectionFromElement(event.currentTarget);
        let parameter = sectionInfo?.section_id || null;

        if (parameter !== null) {
            parameter = parameterFromReportType(parameter, reportType);
        }

        /* eslint-disable camelcase */
        const details = {
            report_id: reportID,
            button_id: event.currentTarget.id,
        };
        /* eslint-enable camelcase */
        logEvent("report", reportFormat, parameter, JSON.stringify(details));
    }
}

function parameterFromReportType(baseParameter, reportType) {
    let parameter = baseParameter;
    if (reportType === "ecp") {
        parameter += LOGGING_CONFIG.PARAMETERS.ECP_SUFFIX;
    }
    return parameter;
}

function clickVisionSimulator(event) {
    const [reportFormat, reportID, reportType] = getReportDetailsFromURL();
    if (reportFormat !== null) {
        const eventID = event.currentTarget.id;
        const parameter = parameterFromReportType(
            LOGGING_CONFIG.PARAMETERS.VISION_SIMULATOR,
            reportType
        );
        const details = parseReportDetails(
            reportID,
            eventID,
            event.currentTarget
        );
        if (details !== null) {
            logEvent(
                "report",
                reportFormat,
                parameter,
                JSON.stringify(details)
            );
        }
    }
}

function hoverMeyeTooltip(event) {
    const [reportFormat, reportID, reportType] = getReportDetailsFromURL();
    if (reportFormat !== null) {
        const eventID = event.currentTarget.id;
        const parameter = parameterFromReportType(
            LOGGING_CONFIG.PARAMETERS.TOOLTIP,
            reportType
        );
        const details = parseReportDetails(
            reportID,
            eventID,
            event.currentTarget
        );
        if (details !== null) {
            logEvent(
                "report",
                reportFormat,
                parameter,
                JSON.stringify(details)
            );
        }
    }
}

function clickMeyeAccordion(event) {
    const accordionButton = event.currentTarget;
    const [reportFormat, reportID, reportType] = getReportDetailsFromURL();

    let toggleValue = accordionButton.getAttribute("data-toggle");
    if (toggleValue !== null) {
        toggleValue = toggleValue === "collapse" ? "expand" : "collapse";
        accordionButton.setAttribute("data-toggle", toggleValue);
    }

    if (
        reportFormat !== null &&
        (accordionButton.ariaExpanded === "true" ||
            accordionButton.ariaSelected === "true" ||
            toggleValue === "expand")
    ) {
        const eventID = event.currentTarget.id;
        const parameter = parameterFromReportType(
            LOGGING_CONFIG.PARAMETERS.ACCORDION,
            reportType
        );
        const details = parseReportDetails(
            reportID,
            eventID,
            event.currentTarget
        );
        if (details !== null) {
            logEvent(
                "report",
                reportFormat,
                parameter,
                JSON.stringify(details)
            );
        }
    }
}

function clickEyeToggle(event) {
    const toggleButton = event.currentTarget;
    const [reportFormat, reportID, reportType] = getReportDetailsFromURL();
    if (toggleButton.ariaSelected === "true" && reportFormat !== null) {
        const eventID = event.currentTarget.id;
        const parameter = parameterFromReportType(
            LOGGING_CONFIG.PARAMETERS.EYE_TOGGLE,
            reportType
        );
        const details = parseReportDetailsWithEye(
            reportID,
            eventID,
            event.currentTarget
        );
        if (details !== null) {
            logEvent(
                "report",
                reportFormat,
                parameter,
                JSON.stringify(details)
            );
        }
    }
}

function clickContentCta(event) {
    const href = event.target.getAttribute("href");
    logEvent(EVENT_TYPE_CONTENT, "click-cta", event.target.id, href);
}

function clickContentQuestion(event) {
    logEvent(EVENT_TYPE_CONTENT, "expand-question", event.currentTarget.id, "");
}

function parseReportDetails(reportID, eventID, targetElement) {
    const response = sectionFromElementID(targetElement, false);
    if (response !== null) {
        // eslint-disable-next-line camelcase
        response.report_id = reportID;
        return response;
    }
    return null;
}

function parseReportDetailsWithEye(reportID, eventID, targetElement) {
    const response = sectionFromElementID(targetElement, true);
    if (response !== null) {
        // eslint-disable-next-line camelcase
        response.report_id = reportID;
        return response;
    }
    return null;
}

function parseEyeFromEventID(eventID) {
    if (eventID.includes("left")) {
        return "left";
    } else if (eventID.includes("right")) {
        return "right";
    }
    return null;
}

function sectionFromElementID(targetElement, parseEye) {
    const elementID = targetElement.id;
    for (const mapping of ELEMENT_ID_TO_SECTION_MAPPING) {
        /* eslint-disable camelcase */
        if (mapping.patterns.some((pattern) => elementID.includes(pattern))) {
            const response = {
                section_id: mapping.sectionID,
                subsection_id: mapping.subsectionID,
                eye: null,
            };
            if (mapping.parseEye || parseEye) {
                response.eye = parseEyeFromEventID(elementID);
            }
            if (mapping.subsectionSource !== undefined) {
                response.subsection_id = targetElement.getAttribute(
                    mapping.subsectionSource
                );
            }
            return response;
        }
        /* eslint-enable camelcase */
    }

    return null;
}

function navigationSectionFromElement(targetElement) {
    const elementID = targetElement.id;
    for (const mapping of NAVIGATION_ID_TO_SECTION_MAPPING) {
        /* eslint-disable camelcase */
        if (mapping.patterns.some((pattern) => elementID.includes(pattern))) {
            const response = {
                section_id: mapping.sectionID,
                subsection_id: mapping.subsectionID,
            };

            if (mapping.subsectionSource !== undefined) {
                response.subsection_id = targetElement.getAttribute(
                    mapping.subsectionSource
                );
            }
            return response;
        }
        /* eslint-enable camelcase */
    }

    return null;
}
